import { z } from "zod";

import type { WorkflowActionType, WorkflowTriggerType } from "@blis/prisma";

export type { WorkflowActionType, WorkflowTriggerType };

// Authorable actions = those a user can fully configure in the admin UI. This
// map is the single source for both the authoring schema (below) and the admin
// UI dropdown that derives its options from it.
//
// Intentionally excluded:
// - ADD_CALCULATED_RESULT / EXECUTE_REFLEX_TEST: not implemented (no handler).
// - ADD_TEST_ORDER_RESULT: backend handler exists, but it has no UI payload
//   editor yet, so it is not user-authorable. Its handler stays registered in
//   `packages/services/workflows` for existing/seeded workflows.
//
// The dispatcher skips any unknown action, so dropping an action here is safe.
export const actions = {
  ADD_RESULT_COMMENT: "Add result comment",
  ADD_REFLEX_TEST: "Add reflex test",
} as const satisfies Partial<Record<WorkflowActionType, string>>;

export const events: Record<WorkflowTriggerType, string> = {
  INITIAL_RESULT: "Initial result",
  FINAL_RESULT: "Final result",
};

export const conditionSchema = z.object({
  fact: z.string().min(1, "Fact is required"),
  operator: z.enum(
    [
      "eq",
      "neq",
      "contains",
      "notContains",
      "match",
      "notMatch",
      "gt",
      "gte",
      "lt",
      "lte",
    ],
    {
      error: "Required",
    },
  ),
  value: z.string().min(1, "Value is required").or(z.number()),
});
export type Condition = z.infer<typeof conditionSchema>;

export const conditionsSchema = z.array(
  z.object({
    or: z.array(conditionSchema).min(1),
  }),
);
export type Conditions = z.infer<typeof conditionsSchema>;

const stepCreateSchema = z.object({
  action: z.enum(
    Object.keys(actions) as [keyof typeof actions, ...(keyof typeof actions)[]],
  ),
  payload: z.any(),
});

export const create = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  trigger: z.enum(
    Object.keys(events) as [WorkflowTriggerType, ...WorkflowTriggerType[]],
  ),
  condition: conditionsSchema.nullish(),
  steps: z.array(stepCreateSchema),
});

const stepUpdateSchema = stepCreateSchema.extend({
  id: z.number().optional(),
});

export const update = create.omit({ steps: true }).extend({
  id: z.number(),
  enabled: z.boolean().prefault(true),
  steps: z.array(stepUpdateSchema),
});

export const createAssociation = z.object({
  workflowId: z.number(),
  testResultSpecId: z.number().optional(),
  testSpecId: z.number().optional(),
});

export const deleteAssociation = z.object({
  id: z.number(),
});
